import { ALL_SALARY_GROUPS_SHALLOW_QUERY } from '@/modules/salary/queries'
import { getNodes } from '@/utils/graphql'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'

interface CommonProps extends ComboboxCommonProps<string> {
  exclude?: string[]
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  clearable?: boolean
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  clearable?: false
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function SalaryGroupCombobox({
  mode,
  value,

  exclude = [],
  clearable = false,

  placeholder,
  searchPlaceholder,
  noResultsText,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    single: {
      placeholder: 'components.combobox.salary-group.placeholder',
      searchPlaceholder: 'components.combobox.salary-group.search-placeholder',
      noResults: 'components.combobox.salary-group.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.salary-group.placeholder',
      searchPlaceholder:
        'components.multi-combobox.salary-group.search-placeholder',
      noResults: 'components.multi-combobox.salary-group.no-results',
    },
  })

  const { data } = useQuery(ALL_SALARY_GROUPS_SHALLOW_QUERY)

  const salaryGroupItems = getNodes(data?.allSalaryGroups)
    .filter(salaryGroup => !exclude.includes(salaryGroup.id))
    .map<ComboboxItem>(salaryGroup => ({
      value: salaryGroup.id,
      label: salaryGroup.name,
    }))

  if (mode === 'single')
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={salaryGroupItems}
        clearable={clearable}
        placeholder={placeholder ?? translations.single.placeholder}
        searchPlaceholder={
          searchPlaceholder ?? translations.single.searchPlaceholder
        }
        noResultsText={noResultsText ?? translations.single.noResults}
        onChange={onChange}
      />
    )

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={salaryGroupItems}
      placeholder={placeholder ?? translations.multiple.placeholder}
      searchPlaceholder={
        searchPlaceholder ?? translations.multiple.searchPlaceholder
      }
      noResultsText={noResultsText ?? translations.multiple.noResults}
      onChange={onChange}
    />
  )
}
